import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import { IDrawingTool } from '@live/pages/live-camera-page/components';
import { FilterGroupType } from '@live/pages/live-camera-page/components/live-player-wrapper/interfaces';

import { IAction } from '@common/modules/shared/interfaces';

import * as SpatialActions from '../actions/live-spatial.actions';

import ISpatialAnalysisRule = Microsoft.VideoIndexer.Live.Contracts.ISpatialAnalysisRule;
import ILiveMonitoredZone = Microsoft.VideoIndexer.Live.Contracts.ILiveMonitoredZone;

export enum SpatialRuleType {
  LINE = 'line',
  AREA = 'area'
}

export const IN_PROGRESS_SHAPE_ID = 'in-progress-shape-id';

export interface ISpatialDrawingState extends EntityState<ISpatialAnalysisRule> {
  selectedTool: IDrawingTool | null;
  isEditMode: boolean;
  isDrawing: boolean;
  inProgressShape: ISpatialAnalysisRule;
  currentEditableRuleId: string;
  isLoaded: boolean;
  isError: boolean;
  counterFilters: IAction[];
  boxFilters: IAction[];
  monitoredZones: ILiveMonitoredZone[];
}

export function selectShapeId(shape: ISpatialAnalysisRule): string {
  return shape.id;
}

export const adapter: EntityAdapter<ISpatialAnalysisRule> = createEntityAdapter<ISpatialAnalysisRule>({
  selectId: selectShapeId
});

export const initialSpatialState: ISpatialDrawingState = adapter.getInitialState({
  selectedTool: null,
  isEditMode: false,
  isDrawing: false,
  isLoaded: false,
  isError: false,
  counterFilters: [],
  inProgressShape: null,
  currentEditableRuleId: '',
  boxFilters: [],
  monitoredZones: []
});

export const spatialReducer = createReducer(
  initialSpatialState,
  on(SpatialActions.listSpatialAnalysisRulesSuccess, (state, { rules }) => {
    const firstShape = rules[rules.length - 1];
    return adapter.setAll(rules, {
      ...state,
      isLoaded: true,
      isError: false,
      currentEditableRuleId: firstShape?.id || '',
      inProgressShape: firstShape || null
    });
  }),
  on(SpatialActions.listSpatialAnalysisRulesFailed, state => {
    return adapter.removeAll({ ...state, isLoaded: true, isError: true });
  }),
  on(SpatialActions.createSpatialAnalysisRuleSuccess, (state, { rule }) => {
    return adapter.addOne(rule, { ...state, inProgressShape: rule, currentEditableRuleId: rule.id });
  }),
  on(SpatialActions.createSpatialAnalysisRuleFailed, state => {
    return { ...state, inProgressShape: null };
  }),
  on(SpatialActions.updateSpatialAnalysisRuleSuccess, (state, { rule }) => {
    return adapter.upsertOne(rule, { ...state, inProgressShape: { ...state.inProgressShape, isUpdating: false } });
  }),
  on(SpatialActions.deleteSpatialAnalysisRuleSuccess, (state, { ruleId }) => {
    return adapter.removeOne(ruleId, { ...state, currentEditableRuleId: '', inProgressShape: null });
  }),
  on(SpatialActions.updateCurrentEditableRuleId, (state, { ruleId }) => ({
    ...state,
    currentEditableRuleId: ruleId,
    inProgressShape: state.entities?.[ruleId] ?? null
  })),

  // Monitored zones
  on(SpatialActions.listMonitoredZonesSuccess, (state, { monitoredZones }) => ({
    ...state,
    monitoredZones: monitoredZones
  })),
  on(SpatialActions.createMonitoredZoneSuccess, (state, { monitoredZone }) => ({
    ...state,
    monitoredZones: [...state.monitoredZones, monitoredZone]
  })),

  // Tool selection
  on(SpatialActions.selectDrawingTool, (state, { tool }) => ({
    ...state,
    selectedTool: tool,
    isDrawing: true,
    inProgressShape: {
      id: IN_PROGRESS_SHAPE_ID,
      name: '',
      description: '',
      type: tool.value,
      coordinates: [],
      direction: []
    },
    currentEditableRuleId: IN_PROGRESS_SHAPE_ID
  })),

  on(SpatialActions.clearSelectedTool, state => ({
    ...state,
    selectedTool: null
  })),

  // Edit mode
  on(SpatialActions.enterEditMode, state => ({
    ...state,
    isEditMode: true
  })),

  on(SpatialActions.exitEditMode, (state, { resetInProgressShape }) => ({
    ...state,
    isEditMode: false,
    isDrawing: false,
    selectedTool: null,
    ...(resetInProgressShape && { inProgressShape: null, currentEditableRuleId: '' })
  })),

  // Drawing state
  on(SpatialActions.startDrawing, state => ({
    ...state,
    isDrawing: true,
    selectedTool: null
  })),

  on(SpatialActions.stopDrawing, state => ({
    ...state,
    isDrawing: false
  })),
  on(SpatialActions.completeShape, (state, { shapeData }) => ({
    ...state,
    isDrawing: false,
    inProgressShape: { ...shapeData, isDirty: true },
    currentEditableRuleId: IN_PROGRESS_SHAPE_ID
  })),
  on(SpatialActions.updateInProgressShape, (state, { shapeData }) => {
    if (shapeData) {
      return {
        ...state,
        inProgressShape: { ...shapeData, isDirty: shapeData.isDirty ?? true },
        currentEditableRuleId: shapeData.id || state.currentEditableRuleId
      };
    } else {
      return { ...state, inProgressShape: null, currentEditableRuleId: '' };
    }
  }),
  on(SpatialActions.markInProgressShapeAsUpdating, state => ({
    ...state,
    inProgressShape: { ...state.inProgressShape, isDirty: false, isUpdating: true }
  })),
  on(SpatialActions.selectPlayerFilters, (state, { counters, boxes }) => ({
    ...state,
    counterFilters: [...counters],
    boxFilters: [...boxes]
  })),
  on(SpatialActions.togglePlayerCounters, (state, { counterValues }) => {
    const counterFilters = state.counterFilters.map(counter => {
      return {
        ...counter,
        selected: counterValues.includes(counter.title)
      };
    });
    return {
      ...state,
      counterFilters
    };
  }),
  on(SpatialActions.togglePlayerBoxes, (state, { boxValues }) => {
    const boxFilters = state.boxFilters.map(box => {
      return {
        ...box,
        selected: boxValues.includes(box.title)
      };
    });
    return {
      ...state,
      boxFilters
    };
  }),
  on(SpatialActions.toggleSpatialInsights, (state, { excludedInsights }) => {
    // For spatial insights filtering, we need to update the counter filters
    // to reflect which insights should be excluded from spatial analysis
    const counterFilters = state.counterFilters.map(counter => {
      // If this is a spatial insight filter (group === 'Spatial')
      if (counter.group === FilterGroupType.Spatial) {
        // Set selected to false if the insight is in the excluded list
        return {
          ...counter,
          selected: !excludedInsights.includes(counter.title)
        };
      }
      // Keep non-spatial filters unchanged
      return counter;
    });
    return {
      ...state,
      counterFilters
    };
  })
);
